import type { HTMLAttributes } from 'react';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

export interface SkeletonProps
  extends ComponentProps<HTMLAttributes<HTMLDivElement>> {
  level?: 1 | 2 | 3 | 4 | 5;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const skeletonVariants = variants({
  base: cn('w-full basis-full animate-pulse'),
  variants: {
    level: {
      1: cn('bg-level-01'),
      2: cn('bg-level-02'),
      3: cn('bg-level-03'),
      4: cn('bg-level-04'),
      5: cn('bg-level-05'),
    },
    size: {
      sm: cn('h-sm rounded-sm'),
      md: cn('h-md rounded-md'),
      lg: cn('h-lg rounded-lg'),
      xl: cn('h-xl rounded-xl'),
    },
  },
  defaultVariants: {
    level: 2,
    size: 'md',
  },
});

export function Skeleton({ level, size, ...props }: SkeletonProps) {
  const className = getClassNameOverride(props, 'Skeleton');

  return (
    <div
      role="presentation"
      className={cn(skeletonVariants({ level, size }), className)}
      {...props}
    />
  );
}

Skeleton.displayName = 'Skeleton';
